import { useCallback, useEffect, useState } from 'react';
import { codicesApi } from '../lib/api';

// Loads codices for a workspace and exposes a create action.
export default function useCodices(workspaceId) {
  const [codices, setCodices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCodices = useCallback(
    async (signal) => {
      setLoading(true);
      setError(null);
      try {
        const data = await codicesApi.listByWorkspace(workspaceId, signal);
        setCodices(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [workspaceId]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchCodices(controller.signal);
    return () => controller.abort();
  }, [fetchCodices]);

  const createCodex = useCallback(
    async (title, type = 'general', subtype) => {
      const { id } = await codicesApi.create({ workspaceId, title, type, subtype });
      await fetchCodices();
      return id;
    },
    [workspaceId, fetchCodices]
  );

  // Optimistic rename: update local state immediately (no refetch -> no
  // flicker), persist in background, rollback on failure.
  const patchCodex = useCallback(
    async (id, { title }) => {
      setCodices((prev) => prev.map((c) => (c.id === id ? { ...c, title } : c)));
      try {
        await codicesApi.patch(id, { title });
      } catch (err) {
        await fetchCodices();
        throw err;
      }
      return id;
    },
    [fetchCodices]
  );

  return { codices, loading, error, refetch: fetchCodices, createCodex, patchCodex };
}
